// React
import { ReactElement, useEffect } from 'react';
// Next
import { useTranslation } from 'next-i18next';
// Context
import { useCountry } from '@utmb/contexts/CountryContext';
// Design System
import { Font } from '@utmb/design-system/text/font';
import { Input } from '@utmb/design-system/input/Input';
import { Radio } from '@utmb/design-system/radio/Radio';
import { InputMessage } from '@utmb/design-system/input-message/InputMessage';
// Types
import { RegistrationSubFormProps } from '@utmb/types/registration-form';
import { GenderEnum } from '@utmb/types/enum/GenderEnum';
// Styles
import styles from './user-information.module.scss';
import { useRouter } from 'next/router';
import { SelectCountry } from '@utmb/features/select-country/SelectCountry';
import { BirthdateInput } from '@utmb/features/birthdate-input/BirthdateInput';

export const MemberForm = (props: RegistrationSubFormProps): ReactElement => {
    const { formik } = props;

    const { t } = useTranslation();
    const { locale } = useRouter();

    const { countries, initCountries } = useCountry();

    useEffect(() => {
        initCountries();
    }, []);

    const formatDate = (dateString: string | null): string => {
        if (!dateString) return '';

        //const date = new Date(dateString);
        //To avoid timezone problems :
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        // On formate la date avec toLocaleDateString en utilisant les options
        return date.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    };

    return (
        <>
            <div className={`${styles.row} row`}>
                <div className="col-12">
                    <Font.OxaniumRegular className={styles.label} mobile="12" desktop="12">
                        {t('common.gender.label')} *
                    </Font.OxaniumRegular>
                    <ul className={styles.radio_list}>
                        <li className={styles.radio_item_container}>
                            <Radio
                                id="genderMale"
                                name="gender"
                                label={t('common.gender.men')}
                                value={GenderEnum.MALE}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </li>
                        <li className={styles.radio_item_container}>
                            <Radio
                                id="genderWoman"
                                name="gender"
                                label={t('common.gender.women')}
                                value={GenderEnum.FEMALE}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                            />
                        </li>
                    </ul>
                    {formik.touched.gender && formik.errors.gender && <InputMessage type="error" message={formik.errors.gender} />}
                </div>
            </div>
            <div className={`${styles.row} row`}>
                <div className="col-12 col-md-6">
                    <Input
                        id="firstName"
                        name="firstName"
                        type="text"
                        label={t('common.firstName')}
                        placeholder={t('common.firstName')}
                        value={formik.values.firstName}
                        onchange={formik.handleChange}
                        onblur={formik.handleBlur}
                        require={true}
                    />
                    {formik.touched.firstName && formik.errors.firstName && <InputMessage type="error" message={formik.errors.firstName} />}
                </div>
                <div className="col-12 col-md-6">
                    <Input
                        id="lastName"
                        name="lastName"
                        type="text"
                        label={t('common.lastName')}
                        placeholder={t('common.lastName')}
                        value={formik.values.lastName}
                        onchange={formik.handleChange}
                        onblur={formik.handleBlur}
                        require={true}
                    />
                    {formik.touched.lastName && formik.errors.lastName && <InputMessage type="error" message={formik.errors.lastName} />}
                </div>
            </div>
            <div className={`${styles.row} row`}>
                <div className="col-12 col-md-6">
                    <BirthdateInput
                        onBlur={formik.handleBlur}
                        onChange={(value) => formik.getFieldHelpers('birthdate').setValue(value)}
                        value={formik.values.birthdate}
                    />
                    <span style={{ fontStyle: 'italic', fontSize: '0.9em' }}>{formatDate(formik.values.birthdate)}</span>
                    {formik.touched.birthdate && formik.errors.birthdate && <InputMessage type="error" message={formik.errors.birthdate} />}
                </div>
                <div className={`col-12 col-md-6 ${styles.wrapNationality}`}>
                    <Font.OxaniumRegular as="span" mobile="12" desktop="12" className={styles.label}>
                        {t('myutmb:myUtmb.myInformation.basicInformation.nationality') + ' *'}
                    </Font.OxaniumRegular>

                    <SelectCountry
                        countries={countries.map((entry) => ({
                            label: entry.name,
                            value: entry.code,
                        }))}
                        onChange={(data: { value: string; label: string }) => {
                            formik.getFieldHelpers('nationality').setValue(data.value);
                        }}
                        value={(() => {
                            const c = countries.find((t) => t.code === formik.values.nationality);
                            return c ? { label: c.name, value: c.code } : undefined;
                        })()}
                    />

                    {formik.touched.nationality && formik.errors.nationality && <InputMessage type="error" message={formik.errors.nationality} />}
                </div>
            </div>
        </>
    );
};
